import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { LOCAL_AGENT_NODE_DEFAULT_BASE_URL } from '@/features/assistant/bridge/local-agent-node-protocol'
import { cn } from '@/shared/ui/cn'

interface LocalMcpStatusResponse {
  ok: true
  protocolVersion: string
  totalSessions: number
  activeSession: {
    sessionId: string
    appOrigin: string
    appVersion: string
    lastSeenAt: string
    registeredAt: string
    lifecycle: {
      type: string
      currentProjectId: string | null
      currentWorkspaceName: string | null
      currentEditorWorkspace: string | null
      isUserSignedIn: boolean
      subscriptionTier: string | null
      protocolVersion: string
    }
  } | null
}

type DetectState = 'idle' | 'loading' | 'success' | 'error'

function StatusPill({
  tone,
  children,
}: {
  tone: 'success' | 'warning' | 'muted'
  children: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
        tone === 'success' && 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
        tone === 'warning' && 'border-amber-500/30 bg-amber-500/10 text-amber-300',
        tone === 'muted' && 'border-white/10 bg-white/5 text-muted-foreground',
      )}
    >
      {children}
    </span>
  )
}

function formatDateTime(value: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('zh-CN', { hour12: false })
}

async function copyTextToClipboard(text: string, successMessage: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text)
    toast.success(successMessage)
  } catch (error) {
    toast.error('复制失败', {
      description: error instanceof Error ? error.message : '无法写入剪贴板',
    })
  }
}

export function LocalMcpStatusPanel() {
  const [detectState, setDetectState] = useState<DetectState>('idle')
  const [status, setStatus] = useState<LocalMcpStatusResponse | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const detectBridge = useCallback(async () => {
    setDetectState('loading')
    setErrorMessage(null)

    try {
      const response = await fetch(`${LOCAL_AGENT_NODE_DEFAULT_BASE_URL}/api/local-agent/status`, {
        method: 'GET',
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const payload = (await response.json()) as LocalMcpStatusResponse
      setStatus(payload)
      setDetectState('success')
    } catch (error) {
      setStatus(null)
      setDetectState('error')
      setErrorMessage(error instanceof Error ? error.message : 'unknown_error')
    }
  }, [])

  useEffect(() => {
    void detectBridge()
  }, [detectBridge])

  const activeSession = status?.activeSession ?? null
  const lifecycle = activeSession?.lifecycle ?? null

  const readiness = useMemo(() => {
    const bridgeOnline = detectState === 'success'
    const browserConnected = Boolean(activeSession)
    const browserOriginMatched = activeSession?.appOrigin === window.location.origin
    const projectReady = Boolean(lifecycle?.currentProjectId)
    const workspaceReady = Boolean(
      lifecycle?.currentWorkspaceName || lifecycle?.currentEditorWorkspace,
    )
    const signedIn = lifecycle?.isUserSignedIn ?? false

    return {
      bridgeOnline,
      browserConnected,
      browserOriginMatched,
      projectReady,
      workspaceReady,
      signedIn,
      canUseMcp: bridgeOnline && browserConnected && browserOriginMatched && projectReady,
    }
  }, [activeSession, detectState, lifecycle])

  // MCP 配置片段（通用 TOML 格式）
  const mcpConfigSnippet = useMemo(
    () => `[mcp_servers.fastcut]
command = "npx"
args = ["--yes", "@fastcut-video/mcp-agent"]
enabled = true
startup_timeout_ms = 15000
tool_timeout_sec = 300

[mcp_servers.fastcut.env]
VIDEOCUT_LOCAL_AGENT_PORT = "4174"
VIDEOCUT_LOCAL_AGENT_REQUEST_TIMEOUT_MS = "240000"`,
    [],
  )

  // 给 Codex 的完整提示词
  const codexSetupPromptSnippet = useMemo(() => {
    const projectHint = lifecycle?.currentProjectId
      ? `当前 FastCut 项目 ID 是 ${lifecycle.currentProjectId}。`
      : '当前已经打开 FastCut 项目，请基于当前项目直接修改。'
    const workspaceHint = lifecycle?.currentWorkspaceName
      ? `工作区名称是 ${lifecycle.currentWorkspaceName}。`
      : lifecycle?.currentEditorWorkspace
        ? `编辑器工作区是 ${lifecycle.currentEditorWorkspace}。`
        : '请先读取当前项目和时间线状态，再开始操作。'

    return `请按照下面的 MCP 配置帮我自动配置 FastCut MCP。

如果本机还没有安装，npx 会自动从 npm 下载 @fastcut-video/mcp-agent 并运行，不需要手动安装。

请使用下面这段配置：

\`\`\`toml
${mcpConfigSnippet}
\`\`\`

配置完成后，请使用 fastcut MCP 工具直接操作我当前打开的 FastCut 项目，不要新建项目，不要导出，不要生成新的外部素材，优先复用当前媒体和时间线。

先检查当前项目、轨道和选中素材状态，再执行修改。
${projectHint}
${workspaceHint}
当前页面来源是 ${window.location.origin}。

如果某一步工具不可用或前置条件不满足，请先告诉我缺什么，再继续下一步。`
  }, [lifecycle, mcpConfigSnippet])

  return (
    <div className="space-y-5">
      {/* 标题区域 */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h3 className="text-sm font-medium">FastCut MCP 自动化</h3>
          <p className="text-xs text-muted-foreground">
            将 AI 客户端连接到当前 FastCut 项目，自动执行剪辑操作。
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-8 shrink-0"
          onClick={() => {
            void detectBridge()
          }}
          disabled={detectState === 'loading'}
        >
          {detectState === 'loading' ? '检测中...' : '重新检测'}
        </Button>
      </div>

      {/* 连接状态卡片 */}
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">本地 Bridge 服务</p>
            <StatusPill tone={readiness.bridgeOnline ? 'success' : 'warning'}>
              {readiness.bridgeOnline ? '在线' : '未连接'}
            </StatusPill>
          </div>
          <p className="text-sm font-medium">{LOCAL_AGENT_NODE_DEFAULT_BASE_URL}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {readiness.bridgeOnline
              ? `协议版本 ${status?.protocolVersion ?? '-'}`
              : 'Bridge 未启动，请先启动本地 agent。'}
          </p>
        </div>

        <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">当前浏览器会话</p>
            <StatusPill tone={readiness.browserConnected ? 'success' : 'warning'}>
              {readiness.browserConnected ? '已连接' : '未连接'}
            </StatusPill>
          </div>
          <p className="text-sm font-medium truncate">
            {activeSession?.appOrigin ?? window.location.origin}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {readiness.browserConnected
              ? `最后心跳 ${formatDateTime(activeSession?.lastSeenAt ?? null)}`
              : '当前页面还没有被本地 Bridge 接管。'}
          </p>
        </div>
      </div>

      {/* 项目就绪状态 */}
      <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone={readiness.canUseMcp ? 'success' : 'warning'}>
            {readiness.canUseMcp ? 'MCP 可用' : 'MCP 未就绪'}
          </StatusPill>
          <StatusPill tone={readiness.browserOriginMatched ? 'success' : 'muted'}>
            {readiness.browserOriginMatched ? '页面来源匹配' : '页面来源不匹配'}
          </StatusPill>
          <StatusPill tone={readiness.projectReady ? 'success' : 'muted'}>
            {readiness.projectReady ? '项目已打开' : '未打开项目'}
          </StatusPill>
          <StatusPill tone={readiness.workspaceReady ? 'success' : 'muted'}>
            {readiness.workspaceReady ? '工作区已就绪' : '工作区未就绪'}
          </StatusPill>
          <StatusPill tone={readiness.signedIn ? 'success' : 'muted'}>
            {readiness.signedIn ? '用户已登录' : '访客模式'}
          </StatusPill>
        </div>

        <Separator className="my-3 bg-white/8" />

        <div className="grid gap-2 text-xs text-muted-foreground md:grid-cols-2">
          <div>
            <span className="text-foreground/80">当前项目 ID：</span>
            {lifecycle?.currentProjectId ?? '-'}
          </div>
          <div>
            <span className="text-foreground/80">订阅层级：</span>
            {lifecycle?.subscriptionTier ?? '-'}
          </div>
          <div>
            <span className="text-foreground/80">工作区名称：</span>
            {lifecycle?.currentWorkspaceName ?? '-'}
          </div>
          <div>
            <span className="text-foreground/80">编辑器工作区：</span>
            {lifecycle?.currentEditorWorkspace ?? '-'}
          </div>
          <div>
            <span className="text-foreground/80">活跃会话 ID：</span>
            {activeSession?.sessionId ?? '-'}
          </div>
          <div>
            <span className="text-foreground/80">浏览器版本：</span>
            {activeSession?.appVersion ?? '-'}
          </div>
        </div>
      </div>

      {/* 快速启动指南 */}
      <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-3 text-xs text-muted-foreground">
        <p className="text-sm font-medium text-foreground/90 mb-2">快速启动</p>
        <div className="space-y-2">
          <p>
            <span className="text-foreground/60">1.</span> 确保本机已安装 Node.js 18+
          </p>
          <p>
            <span className="text-foreground/60">2.</span> 不需要手动安装，MCP 客户端会通过{' '}
            <code className="px-1 py-0.5 bg-white/5 rounded text-emerald-300">npx</code> 自动下载运行
          </p>
          <p>
            <span className="text-foreground/60">3.</span> 将下方配置复制到你的 MCP 客户端（Codex / ZCode / Cursor 等）
          </p>
        </div>
        {detectState === 'error' && (
          <p className="mt-3 text-amber-300">最近一次检测失败：{errorMessage}</p>
        )}
      </div>

      {/* MCP 配置卡片 */}
      <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-medium text-foreground/90">MCP 配置</p>
            <p className="mt-1 text-xs text-muted-foreground">
              标准 TOML 格式，适用于 Codex、ZCode、Cursor 等支持 MCP 的客户端。
            </p>
          </div>
          <div className="flex gap-2 shrink-0 self-start">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => {
                void copyTextToClipboard(mcpConfigSnippet, 'MCP 配置已复制')
              }}
            >
              复制配置
            </Button>
            <Button
              variant="default"
              size="sm"
              className="h-8"
              onClick={() => {
                void copyTextToClipboard(codexSetupPromptSnippet, 'Codex 配置提示词已复制')
              }}
            >
              复制给 AI
            </Button>
          </div>
        </div>
        <pre className="mt-3 max-h-[280px] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-white/8 bg-black/30 p-3 text-[11px] leading-5 text-foreground/90">
          <code>{mcpConfigSnippet}</code>
        </pre>
      </div>

      {/* 底部说明 */}
      <div className="text-[11px] text-muted-foreground/70 space-y-1">
        <p>• 不再需要设置 <code className="text-white/50">FASTCUT_APP_ROOT</code> 或本地源码目录</p>
        <p>• Bridge 默认使用端口 4174，可通过环境变量 <code className="text-white/50">VIDEOCUT_LOCAL_AGENT_PORT</code> 修改</p>
        <p>• 支持的客户端：Codex、ZCode、Cursor、Claude Desktop 等所有 MCP 兼容客户端</p>
      </div>
    </div>
  )
}